<template>
    <div
        v-if="!req.deleted"
        class="px-4 md:px-6 py-2 md:py-3 border border-gray-300 rounded shadow hover:shadow-xl transition duration-150"
    >
        <modal
            :closable="true"
            @close="closeGivModal"
            title="Register Giv"
            :open="registerGivModalOpen"
        >
            <div slot="content">
                <span
                    class="my-12 flex items-center justify-center w-full border border-transparent bg-green-400 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                    v-if="registeredGiv"
                >
                    <svg
                        class="w-5 h-5 text-white mr-1"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    {{ $t('Submitted') }}</span
                >
                <form
                    v-else
                    class="md:pt-6 md:pb-10 space-y-2 md:space-y-4"
                    @submit.prevent="createGiv"
                >
                    <div class="md:flex items-center space-y-2 md:space-y-0">
                        <section class="flex-1 space-y-1 flex flex-col items-center">
                            <label for="givGiver" class="text-sm">{{ formGivGiver.name }}</label>

                            <div
                                class="w-12 h-12 flex-shrink-0 rounded-full border-4 border-gray-200 overflow-hidden"
                            >
                                <img
                                    loading="lazy"
                                    class="object-cover"
                                    :src="parseUrl(formGivGiver.photoURL)"
                                />
                            </div>
                        </section>
                        <svg
                            class="hidden md:block w-6 h-6 mx-4 text-gray-300"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                        </svg>
                        <section class="flex-1 space-y-1 flex flex-col items-center">
                            <label for="givReceiver" class="text-sm">{{
                                formGivReceiver.name
                            }}</label>
                            <div
                                class="w-12 h-12 flex-shrink-0 rounded-full border-4 border-gray-200 overflow-hidden"
                            >
                                <img
                                    loading="lazy"
                                    class="object-cover"
                                    :src="parseUrl(formGivReceiver.photoURL)"
                                />
                            </div>
                        </section>
                    </div>
                    <div class="flex items-center space-x-3">
                        <section class="flex-1 space-y-1 flex flex-col">
                            <label for="givKind" class="text-sm">{{ $t('Giv Type') }}</label>
                            <select
                                :disabled="registeringGiv || registeredGiv"
                                v-model="formGivType"
                                name="givKind"
                                class="form-select"
                            >
                                <option :label="$t('Meetup')">Meetup</option>
                                <option :label="$t('Mail')">Mail</option>
                            </select>
                        </section>
                        <section class="flex-1 space-y-1 flex flex-col">
                            <label for="givDate" class="text-sm">{{ $t('Date') }}</label>
                            <input
                                :disabled="registeringGiv || registeredGiv"
                                v-model="formGivDate"
                                name="givDate"
                                type="date"
                                class="w-full form-input"
                            />
                        </section>
                    </div>
                    <section class="space-y-1 flex flex-col">
                        <span
                            v-if="registerGivError"
                            class="bg-red-100 text-center text-sm py-1 px-1 rounded shake text-red-900 max-w-3xl overflow-auto"
                        >
                            {{ this.registerGivError }}
                        </span>
                    </section>
                    <div>
                        <button
                            class="flex items-center justify-center w-full border border-transparent hover:bg-transparent hover:border-blue-500 hover:text-gray-900 transition duration-150 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                            :class="[
                                registeringGiv || !this.formValid
                                    ? 'pointer-events-none bg-gray-200'
                                    : 'bg-blue-500',
                            ]"
                            type="submit"
                        >
                            <spinner v-if="registeringGiv" class="text-white" />
                            <span v-else>{{ $t('Register Giv') }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </modal>
        <modal @close="closeDeleteModal" :closable="!deleting" :open="deleteModalOpen">
            <div slot="header">
                <h2 class="pt-2 text-xl font-semibold text-red-600">
                    {{ $t('Delete Request') }}
                </h2>
            </div>
            <div slot="content">
                <form class="mt-4" @submit.prevent="deleteGivRequest">
                    <p class="bg-red-100 rounded-sm px-3 py-4">
                        Are you sure you want to delete this request: id: {{ req.id }}
                        <br />
                        <span class="uppercase tracking-wide text-sm font-medium"
                            >This action cannot be reversed!!</span
                        >
                    </p>
                    <p v-if="!!deleteError" class="my-2 error-msg">{{ deleteError }}</p>
                    <div class="px-2 mt-6 mb-2 flex justify-between">
                        <Button
                            :disabled="deleting"
                            @click="closeDeleteModal"
                            class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                            type="button"
                            :label="$t('Cancel')"
                        />
                        <Button
                            :disabled="deleting"
                            :loading="deleting"
                            class="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded"
                            :label="$t('Yes Delete')"
                        />
                    </div>
                </form>
            </div>
        </modal>
        <div class="flex flex-col lg:flex-row">
            <div class="w-30 border-r border-gray-200 pr-6 flex flex-col justify-end space-y-2">
                <div class="space-x-2">
                    <span
                        :class="
                            `${
                                req.status === 'match'
                                    ? 'bg-indigo-500 text-white border-indigo-400'
                                    : 'opacity-50 bg-gray-200 text-gray-400'
                            } inline-flex items-center border rounded shadow-xl text-xs px-2 py-1`
                        "
                    >
                        {{ $t('Match') }}
                    </span>
                    <span
                        :class="
                            `${
                                !!req.givId
                                    ? 'bg-green-500 text-white border-green-500'
                                    : 'opacity-50 bg-gray-200 text-gray-400'
                            } inline-flex items-center border rounded shadow-xl text-xs px-2 py-1`
                        "
                    >
                        {{ $t('Giv Created') }}
                    </span>
                </div>
                <span class="text-xs font-light">{{ parseTimestamp(req.createdAt) }}</span>
            </div>
            <div class="flex-1 lg:flex items-center justify-between">
                <div class="lg:ml-8 flex-1 flex items-center">
                    <div class="flex-1 flex items-center justify-start">
                        <div
                            class="w-12 h-12 flex-shrink-0 rounded-full border-4 border-gray-200 overflow-hidden"
                        >
                            <img
                                loading="lazy"
                                alt="req.fromUser.lastName"
                                class="object-cover"
                                :src="parseUrl(req.fromUser.photoURL)"
                            />
                        </div>
                        <span class="ml-2">{{ req.fromUser.name }} </span>
                    </div>
                    <svg
                        class="flex-1 w-6 h-6 text-gray-300"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                    <div class="flex-1 flex items-center justify-start">
                        <div
                            class="w-12 h-12 flex-shrink-0 rounded-full border-4 border-gray-200 overflow-hidden"
                        >
                            <img
                                loading="lazy"
                                alt="req.toUser.lastName"
                                class="object-cover"
                                :src="parseUrl(req.toUser.photoURL)"
                            />
                        </div>
                        <span class="ml-2">{{ req.toUser.name }} </span>
                    </div>
                </div>

                <div class="flex items-center justify-center py-3 lg:py-0 lg:justify-end space-x-2">
                    <button
                        :disabled="req.status !== 'match' || !!req.givId"
                        :class="
                            `${
                                req.status === 'match' && !req.givId
                                    ? 'hover:border-indigo-600 text-indigo-600 hover:text-white border-indigo-600 hover:bg-indigo-600 '
                                    : 'cursor-not-allowed border-gray-200 text-gray-300'
                            } flex items-center rounded border border text-xs px-4 py-2 rounded-sm`
                        "
                        @click="registerGivModalOpen = true"
                    >
                        {{ $t('Register Giv') }}
                    </button>
                    <button
                        @click="deleteModalOpen = true"
                        class="flex items-center text-red-600 border-transparent hover:bg-red-600 border hover:border-red-600 hover:text-white text-xs px-3 py-px rounded-sm"
                    >
                        <svg
                            class="w-4 h-4 group-hover:text-red-100 text-red-300 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            ></path>
                        </svg>
                        {{ $t('Delete') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatDistanceToNow } from 'date-fns'
import api from '@/lib/api'

//@Todo copy paste form givs
const formatDateInput = d => {
    const result = d.toISOString().split('T')[0]
    return result
}

export default {
    props: ['req'],
    mounted() {},
    computed: {
        formValid() {
            return (
                !!this.formGivGiver &&
                this.formGivGiver !== '' &&
                !!this.formGivReceiver &&
                this.formGivReceiver !== '' &&
                !!this.formGivDate &&
                this.formGivDate !== '' &&
                !!this.formGivType &&
                this.formGivType !== ''
            )
        },
    },
    data() {
        return {
            formGivType: 'Mail',
            formGivGiver: this.req.fromUser,
            formGivReceiver: this.req.toUser,
            formGivDate: formatDateInput(new Date()),

            deleteModalOpen: false,
            deleteError: null,
            deleting: false,

            registerGivModalOpen: false,
            registerGivError: null,
            registeringGiv: false,
            registeredGiv: false,
        }
    },
    methods: {
        closeGivModal() {
            this.registerGivModalOpen = false
            this.registerGivError = null
        },
        closeDeleteModal() {
            this.deleteModalOpen = false
            this.deleteError = null
        },
        async createGiv() {
            this.registeringGiv = true
            try {
                const payload = {
                    giverId: this.formGivGiver.id,
                    receiverId: this.formGivReceiver.id,
                    givRequestId: this.req.id,
                    type: this.formGivType,
                    status: 'finished',
                }
                const giv = await api.createGiv(payload)
                this.registeredGiv = true
                this.req.givId = giv.id
            } catch (err) {
                this.registerGivError = err.message
            }
            this.registeringGiv = false
        },
        async deleteGivRequest() {
            this.deleting = true
            try {
                await api.deleteGivRequest(this.req.id)
                this.deleteModalOpen = false
                this.req.deleted = true
            } catch (err) {
                this.deleteError = err.message
            }
            this.deleting = false
        },
        parseUrl(url) {
            if (!url) return url
            if (url.startsWith('http')) {
                return url
            }
            return `https://cdn.giv.link/${url}`
        },
        parseTimestamp(timestamp) {
            if (!timestamp) return ''
            try {
                return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
            } catch (err) {
                const p = parseInt(timestamp)
                return formatDistanceToNow(new Date(p), { addSuffix: true })
            }
        },
    },
}
</script>
